import { signedFetch } from './client';

export interface FormulaRule {
  ruleId: string;
  name: string;
  status: string;
  predicateType: 'event_filter' | 'threshold';
  scope?: string;
  filter?: Record<string, unknown>;
  accumulate?: { field: string; operation: string };
  trigger?: { operator: string; value: number };
  output?: { store: string; eventType: string };
  aggregateId?: string;
  target?: { type: string; storeName?: string; arn?: string; eventType?: string };
}

export function normalizeRule(raw: any): FormulaRule {
  if (raw.predicate) {
    // Unified format
    const predicate = raw.predicate;
    const action = raw.action || {};
    const isThreshold = predicate.type === 'threshold' || predicate.operator != null;
    const rule: FormulaRule = {
      ruleId: raw.ruleId,
      name: raw.name,
      status: raw.status,
      predicateType: isThreshold ? 'threshold' : 'event_filter',
    };
    if (predicate.filter) rule.filter = predicate.filter;
    if (predicate.aggregateId) rule.aggregateId = predicate.aggregateId;
    if (predicate.operator != null) rule.trigger = { operator: predicate.operator, value: predicate.value };
    if (action.aggregate) {
      rule.scope = 'GLOBAL';
      rule.accumulate = { field: action.aggregate.field, operation: action.aggregate.operation };
    }
    if (action.target) rule.target = action.target;
    return rule;
  }
  // Old format
  return {
    ruleId: raw.ruleId,
    name: raw.name,
    status: raw.status,
    predicateType: raw.trigger ? 'threshold' : 'event_filter',
    scope: raw.scope,
    filter: raw.filter,
    accumulate: raw.accumulate,
    trigger: raw.trigger,
    output: raw.output,
  };
}

export async function listFormulaRules(storeName: string): Promise<FormulaRule[]> {
  const resp = await signedFetch('GET', `/stores/${storeName}/formula-rules`);
  const data = resp.data as { rules?: any[] };
  return (data.rules || []).map(normalizeRule);
}

export interface GlobalAggregate {
  aggregateId: string;
  accumValue: number;
  accumCount: number;
}

export async function getGlobalAggregates(storeName: string, aggregateIds: string[]): Promise<GlobalAggregate[]> {
  const results = await Promise.all(aggregateIds.map(async (aggregateId) => {
    try {
      const resp = await signedFetch('GET', `/stores/${storeName}/events?stream=${encodeURIComponent(`_agg_global#${aggregateId}`)}&limit=1`);
      const data = resp.data as any;
      const events = data?.events || [];
      if (events.length > 0) {
        const item = events[0];
        return { aggregateId, accumValue: Number(item.data?.accumValue ?? item.accumValue ?? 0), accumCount: Number(item.data?.accumCount ?? item.accumCount ?? 0) };
      }
      return { aggregateId, accumValue: 0, accumCount: 0 };
    } catch {
      return { aggregateId, accumValue: 0, accumCount: 0 };
    }
  }));
  return results;
}
